/* Melaninful v4 — press-floor behaviors ported from the approved mockup.
   Rules that must not regress (HANDOFF §13):
   - rails must work with mouse drag + arrows (client v15e: the vertical wheel
     scrolls the PAGE, never a rail — reversed from v4.1);
   - scroll-reveal may tag headings/editorial only, never product grids or
     images, and a 1200ms failsafe force-reveals everything;
   - delivery dates are computed, never hardcoded.
   All initializers are idempotent (data-*-init guards) and re-run on
   shopify:section:load so theme-editor re-renders keep their behaviors. */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---- continuous marquee: announcement bar + green ticker share one engine.
     A [data-marquee="<px/s>"] element holds .marq-track > .marq-set; the set is
     cloned until the track covers 2x the container, then translateX(-1 set)
     loops seamlessly (duration ∝ set width, so speed is constant everywhere).
     Static under reduced motion; clones are aria-hidden. ---- */
  function initMarquee(root) {
    root.querySelectorAll('[data-marquee]').forEach(function (m) {
      if (m.dataset.melInit) return;
      m.dataset.melInit = '1';
      var track = m.querySelector('.marq-track');
      var set = track && track.querySelector('.marq-set');
      if (!set || reducedMotion.matches) return;
      var speed = parseFloat(m.dataset.marquee) || 60; /* px per second */
      var relayout = function () {
        if (!m.isConnected) return;
        var w = set.scrollWidth;
        if (!w) return;
        var need = Math.max(2, Math.ceil((m.clientWidth * 2) / w));
        for (var i = track.querySelectorAll('.marq-set').length; i < need; i++) {
          var c = set.cloneNode(true);
          c.setAttribute('aria-hidden', 'true');
          track.appendChild(c);
        }
        m.style.setProperty('--marq-shift', w + 'px');
        m.style.setProperty('--marq-time', (w / speed).toFixed(2) + 's');
        track.classList.add('marq-on');
      };
      relayout();
      /* web fonts change the set width — re-measure once they land */
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(relayout);
      var t;
      window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(relayout, 150); });
    });
  }

  /* ---- horizontal rails: drag, vertical-wheel remap, arrows ---- */
  function initRails(root) {
    root.querySelectorAll('[data-rail]').forEach(function (rail) {
      if (rail.dataset.melInit) return;
      rail.dataset.melInit = '1';
      var down = false, moved = false, sx = 0, sl = 0;

      rail.addEventListener('pointerdown', function (e) {
        /* review v15: a <select> inside a rail (deal-box tiles) owns the pointer
           while its popup is open — never start a drag from a form control */
        if (e.pointerType !== 'mouse' || e.button !== 0 || (e.target instanceof Element && e.target.closest('select,input,textarea'))) return;
        down = true; moved = false; sx = e.clientX; sl = rail.scrollLeft;
      });
      rail.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - sx;
        if (Math.abs(dx) > 4) { moved = true; rail.classList.add('grabbing'); }
        if (moved) rail.scrollLeft = sl - dx;
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (t) {
        rail.addEventListener(t, function () {
          down = false;
          rail.classList.remove('grabbing');
          /* clear after the drag-ending click has been swallowed, so a later
             keyboard activation (no pointerdown) is never eaten */
          requestAnimationFrame(function () { moved = false; });
        });
      });
      rail.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); }
      }, true);
      /* client v15e: the vertical wheel is the page's — a rail under the cursor
         no longer hijacks it into sideways scrolling (the wall stopped the page
         dead on desktop). Sideways = drag, arrows, trackpad/shift-wheel (native).
         Reverses the v4.1 "rails must work with vertical wheel" rule everywhere. */

      var nav = document.createElement('div');
      nav.className = 'railnav';
      nav.innerHTML = '<button type="button" aria-label="Scroll back">←</button>' +
        '<button type="button" aria-label="Scroll forward">→</button>';
      var prev = nav.children[0], next = nav.children[1];
      var step = function () { return Math.max(rail.clientWidth * 0.7, 180); };
      prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: 'smooth' }); });
      next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: 'smooth' }); });
      rail.after(nav);

      function sync() {
        var max = rail.scrollWidth - rail.clientWidth;
        nav.hidden = max < 8;
        prev.disabled = rail.scrollLeft <= 0;
        next.disabled = rail.scrollLeft >= max - 1;
      }
      rail.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
      if ('ResizeObserver' in window) new ResizeObserver(sync).observe(rail);
    });
  }

  /* ---- computed delivery window: [data-ship] ---- */
  function initShipDates(root) {
    var fmt = function (d) {
      return d.toLocaleDateString(document.documentElement.lang || 'en', { month: 'short', day: 'numeric' });
    };
    var addBiz = function (from, days) {
      var d = new Date(from);
      while (days > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) days--;
      }
      return d;
    };
    root.querySelectorAll('[data-ship]').forEach(function (el) {
      el.textContent = fmt(addBiz(new Date(), 8)) + '–' + fmt(addBiz(new Date(), 12));
    });
  }

  /* ---- scroll reveal: [data-rv] on headings/editorial only; 1200ms failsafe ---- */
  var revealArmed = false;
  function initReveal() {
    if (revealArmed) return;
    var tagged = document.querySelectorAll('[data-rv]');
    if (!tagged.length || !('IntersectionObserver' in window)) return;
    if (reducedMotion.matches) return;
    revealArmed = true;
    var root = document.documentElement;
    root.classList.add('rv');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    tagged.forEach(function (el) { io.observe(el); });
    /* nothing may stay invisible, ever */
    setTimeout(function () { root.classList.add('rvdone'); }, 1200);
  }

  /* ---- mobile menu drawer: focus is moved in, trapped, and restored.
     CRITICAL (client v2): the drawer is authored inside the header section,
     which lives inside .page-wrapper — the very subtree open() makes inert.
     An inert ancestor kills every tap on the menu's own links, so the node is
     relocated to <body> before wiring. Stale copies from theme-editor section
     re-renders are removed first (getElementById returns the fresh in-section
     node, which precedes any body-level leftover in document order). ---- */
  /* pre-launch review: one document-level Escape handler for the mobile menu,
     rebound to the freshest node per init — per-init registration leaked a
     listener on every theme-editor header re-render. */
  var mmenuClose = null;
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && mmenuClose) mmenuClose();
  });
  function initMobileMenu(root) {
    var menu = document.getElementById('mel-mmenu');
    if (!menu) return;
    if (menu.parentElement !== document.body) {
      document.querySelectorAll('body > .mmenu').forEach(function (stale) {
        /* pre-launch review: a stale copy removed while open would strand the
           scroll lock and leave the page inert in the theme editor. */
        if (stale.classList.contains('open')) {
          document.documentElement.classList.remove('mel-lock');
          var pw = document.querySelector('.page-wrapper');
          if (pw) pw.removeAttribute('inert');
        }
        stale.remove();
      });
      document.body.appendChild(menu);
    }
    var pageWrapper = document.querySelector('.page-wrapper');
    var lastTrigger = null;

    var focusables = function () {
      return menu.querySelectorAll('a[href], button, summary, input, [tabindex]:not([tabindex="-1"])');
    };
    /* client v11: slide like the cart drawer — opening unhides, forces a
       reflow so the off-canvas transform paints, then .open transitions it
       in; closing reverses and hides at the panel's transform transitionend
       (500ms timeout guard covers reduced-motion, where none fires). A
       re-open mid-slide-out cancels the pending hide. */
    var hideTimer = null;
    var hideHandler = null;
    var cancelHide = function () {
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      if (hideHandler) { menu.removeEventListener('transitionend', hideHandler); hideHandler = null; }
    };
    var open = function (yes, trigger) {
      cancelHide();
      if (yes) {
        menu.hidden = false;
        void menu.offsetWidth;
        menu.classList.add('open');
      } else {
        menu.classList.remove('open');
        var hide = function () { cancelHide(); menu.hidden = true; };
        hideHandler = function (e) {
          if (e.target === menu && e.propertyName === 'transform') hide();
        };
        menu.addEventListener('transitionend', hideHandler);
        hideTimer = setTimeout(hide, 500);
      }
      document.documentElement.classList.toggle('mel-lock', yes);
      if (pageWrapper) pageWrapper.toggleAttribute('inert', yes);
      document.querySelectorAll('[data-mel-burger]').forEach(function (b) {
        b.setAttribute('aria-expanded', yes ? 'true' : 'false');
      });
      if (yes) {
        lastTrigger = trigger || null;
        var close = menu.querySelector('[data-mel-close]');
        if (close) close.focus();
      } else if (lastTrigger) {
        lastTrigger.focus();
        lastTrigger = null;
      }
    };
    mmenuClose = function () { if (!menu.hidden) open(false); };
    root.querySelectorAll('[data-mel-burger]').forEach(function (b) {
      if (b.dataset.melInit) return;
      b.dataset.melInit = '1';
      b.addEventListener('click', function () { open(true, b); });
    });
    if (!menu.dataset.melInit) {
      menu.dataset.melInit = '1';
      menu.querySelectorAll('[data-mel-close]').forEach(function (b) {
        b.addEventListener('click', function () { open(false); });
      });
      menu.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') return;
        var f = focusables();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    }
  }

  /* ---- PDP variant-select side effects (client v2). The picker's
     synchronous shopify:product:select carries the target variantId:
     (a) optimistic ATC button price — the section-morph round trip lagged
         ~1s, so the button flips instantly from the embedded price map and
         the morph confirms with server truth;
     (b) deal-box auto-size-match — the morph never re-renders the
         mel-dealbox block, so its row selects re-match to the newly
         selected size from the embedded variant→size map;
     (c) client v12: the main product-price too. Horizon confirms a variant
         with a FULL product-page fetch (~400 KB per click, aborted by the
         next click), so on real connections the headline price sat on the
         old variant for seconds — "the price does not update". The paint
         mirrors snippets/price.liquid's standard branch (sale-first, the
         configured order); product-price.js then replaces the container
         with server truth, so any mismatch lives for one round trip.
     Maps are re-read per event — morphs and combined-listing switches
     replace them. ---- */
  var pdpSelectWired = false;
  function paintMainPrice(entry, labels, productId) {
    var scope = productId ? 'product-price[data-product-id="' + productId + '"]' : 'product-price';
    var box = document.querySelector(scope + ' [ref="priceContainer"]');
    if (!box || !entry || !entry.p) return;
    /* volume-pricing markup ("Price range min - max") is not a single price — let the morph paint it */
    if (document.querySelector(scope + ' [ref="volumePricingNote"]')) return;
    var priceEl = box.querySelector('.price-item__group.price') || box.querySelector(':scope > .price');
    var cmpEl = box.querySelector('.compare-at-price');
    var setText = function (el, txt) {
      var last = el.lastChild;
      while (last && last.nodeType !== 3) last = last.previousSibling;
      if (last) last.nodeValue = ' ' + txt; else el.appendChild(document.createTextNode(txt));
    };
    if (entry.c && priceEl && cmpEl) { setText(priceEl, entry.p); setText(cmpEl, entry.c); return; }
    if (!entry.c && priceEl && !cmpEl) { setText(priceEl, entry.p); return; }
    /* structure change (sale ↔ regular): rebuild the standard-branch markup.
       Sale-first is the price block's configured order (templates/product.json
       show_sale_price_first: true); the morph re-orders if the setting flips. */
    var mk = function (cls, label, txt) {
      var s = document.createElement('span');
      s.className = cls;
      if (label) {
        var vh = document.createElement('span');
        vh.className = 'visually-hidden';
        vh.textContent = label + '\u00A0';
        s.appendChild(vh);
      }
      s.appendChild(document.createTextNode(txt));
      return s;
    };
    var frag = document.createDocumentFragment();
    if (entry.c) {
      frag.appendChild(mk('price-item__group price', labels && labels.sale, entry.p));
      frag.appendChild(mk('price-item__group price-item--regular compare-at-price', labels && labels.regular, entry.c));
    } else {
      frag.appendChild(mk('price', null, entry.p));
    }
    var unit = box.querySelector('.unit-price');
    box.textContent = '';
    box.appendChild(frag);
    if (unit) box.appendChild(unit);
  }
  function initPdpSelect() {
    if (pdpSelectWired) return;
    if (!document.querySelector('[data-mel-price-map], [data-mel-xsell-sizes]')) return;
    pdpSelectWired = true;
    var readMap = function (sel) {
      var el = document.querySelector(sel);
      try { return el && JSON.parse(el.textContent); } catch (err) { return null; }
    };
    document.addEventListener('shopify:product:select', function (e) {
      var vid = e && e.detail && e.detail.variantId;
      if (!vid) return;
      var map = readMap('[data-mel-price-map]');
      var entry = map && map.v && map.v[vid];
      if (entry && entry.p) {
        document.querySelectorAll('.atc-price').forEach(function (p) {
          p.textContent = '\u00A0\u00B7\u00A0' + entry.p;
        });
        paintMainPrice(entry, map.labels, e.target && e.target.dataset ? e.target.dataset.productId : '');
      }
      var sizes = readMap('[data-mel-xsell-sizes]');
      var size = sizes && sizes[vid];
      var styles = readMap('[data-mel-xsell-styles]');
      var style = (styles && styles[vid]) || '';
      if (size) {
        /* client v15: the deal-box rail tiles + any quick rows in the box
           follow the PDP's size (and style: Pullover/Zip); tabs fetched later
           read the same values off <mel-deal-tabs>. */
        document.querySelectorAll('mel-deal-tabs').forEach(function (t) { t.dataset.size = size; t.dataset.style = style; });
        document.querySelectorAll('.promo select.qrow-size').forEach(function (sel) { melPickOption(sel, size, style); });
      }
    });
  }

  /* ---- client v14b: the deal box tally counts live. Horizon's quick-add
     and buy button dispatch shopify:cart:lines-update with a promise that
     resolves to the fresh cart (the cart-icon pattern); the tally + tier
     chips repaint from cart.totalQuantity. Tiers ride on the tally's
     data-mel-tally="total:free,…". Copy rule mirrors snippets/mel-deal. ---- */
  /* client v15: pick the <option> whose data-size matches, preferring the one
     whose data-style matches too (Pullover / Zip carried from the PDP), and
     fire change so the delegated price handler repaints — a silent .value
     swap left the row showing the old size's price while Add submitted the
     new one (pre-launch review). */
  function melPickOption(sel, size, style) {
    if (!sel || !size) return false;
    var match = null, first = null;
    for (var i = 0; i < sel.options.length; i++) {
      var o = sel.options[i];
      if (o.dataset.size !== size) continue;
      if (!first) first = o;
      if (style && o.dataset.style === style) { match = o; break; }
    }
    var pick = match || first;
    if (!pick) return false;
    if (sel.value !== pick.value) { sel.value = pick.value; sel.dispatchEvent(new Event('change', { bubbles: true })); }
    return true;
  }

  /* ---- deal state: where the cart stands against the tiers. The spec rides
     on the tally: data-mel-tally="total:free,…" (snippets/mel-deal). Copy rule
     mirrors the Liquid so the live repaint and the server render agree. ---- */
  function melDealState(n, spec) {
    var el = spec ? null : document.querySelector('[data-mel-tally]');
    spec = spec || (el && el.getAttribute('data-mel-tally')) || '';
    if (!spec) return null;
    var tiers = spec.split(',').map(function (t) { var p = t.split(':'); return { at: +p[0], free: +p[1] }; });
    var reached = -1, next = -1;
    tiers.forEach(function (t, i) { if (n >= t.at) reached = i; else if (next === -1) next = i; });
    var copy = next === -1
      ? 'Maxed: ' + tiers[reached].free + (tiers[reached].free === 1 ? ' free print' : ' free prints')
      : 'Add ' + (tiers[next].at - n) + (n > 0 ? ' more' : '') + ', get ' + tiers[next].free + ' free';
    return { reached: reached, done: next === -1, copy: copy, max: tiers[tiers.length - 1].at };
  }
  var dealTallyWired = false;
  function initDealTally() {
    if (dealTallyWired || !document.querySelector('[data-mel-tally]')) return;
    dealTallyWired = true;
    var paint = function (n) {
      document.querySelectorAll('[data-mel-tally]').forEach(function (el) {
        var st = melDealState(n, el.getAttribute('data-mel-tally'));
        if (!st) return;
        el.classList.toggle('done', st.done);
        el.querySelector('span').textContent = 'You have ' + n + (n === 1 ? ' item' : ' items');
        el.querySelector('b').textContent = st.copy;
        var box = el.closest('.promo, .oband');
        if (box) {
          box.querySelectorAll('.tiers span').forEach(function (chip, i) { chip.classList.toggle('on', st.reached >= i); });
          /* collection band (desktop): the ladder is server-rendered Liquid, so move
             its fill and light its stops here the way the drawer re-render would */
          var lad = box.querySelector('.mel-lad');
          if (lad) {
            lad.classList.toggle('done', st.done);
            var fill = lad.querySelector('.lad-fill');
            if (fill) fill.style.width = Math.min(100, (n * 100) / st.max) + '%';
            lad.querySelectorAll('.lad-stop').forEach(function (s, i) { s.classList.toggle('hit', st.reached >= i); });
          }
        }
      });
    };
    document.addEventListener('shopify:cart:lines-update', function (e) {
      if (!e.promise) return;
      e.promise.then(function (r) {
        var n = r && r.cart && r.cart.totalQuantity;
        if (typeof n === 'number') paint(n);
      }).catch(function () {});
    });
  }

  /* ---- client v15: <mel-deal-tabs> — the deal box's theme tabs + fetched
     rail (blocks/mel-dealbox). Each tab is one Section Rendering API call:
     /collections/<theme>?filter.p.product_type=<type>&sort_by=best-selling
     &section_id=mel-deal-rail → the best-selling same-type designs of that
     theme. The PDP's own design is dropped, the list trimmed to data-limit,
     the shopper's size/style pre-selected; loaded tabs are cached for the
     page's life. The skeleton keeps the rail's height so nothing under the
     buy buttons shifts. The quick view (assets/mel-deal.js) is built from
     tile data, never from a fetched product page.

     Bugfix (2026-09-30): the catalog is uneven per type — beanies exist in
     one theme, "Apparel" in three, kid backpacks in none — so a tab loaded on
     tap used to come back empty, vanish under the finger and snap the rail
     back (the "click a theme, nothing shows" report). Now every tab is probed
     AT ONCE when the box nears the viewport (≤ 12 requests — the themes plus
     the block's featured-drop tab — ~100 B each when empty, ~3 KB gzipped
     with tiles, all back in one round-trip); tabs
     without same-type designs hide BEFORE they can be tapped, later taps are
     instant from cache, and a product in no theme no longer walks the list
     one request at a time. Data Saver keeps the old lazy per-tap fetch. ---- */
  if (!window.customElements.get('mel-deal-tabs')) {
    window.customElements.define('mel-deal-tabs', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.melInit) return;
        this.dataset.melInit = '1';
        var self = this;
        this.cache = {};
        this.inflight = {};
        this.tabs = this.querySelector('.dtabs');
        this.rail = this.querySelector('[data-mel-rail]');
        if (!this.tabs || !this.rail) return;
        this.skeleton = this.rail.innerHTML;
        this.rail.setAttribute('data-rail', '');
        initRails(this);
        this.tabs.addEventListener('click', function (e) {
          var b = e.target.closest('[data-theme]');
          if (b) self.select(b.dataset.theme);
        });
        this.tabs.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          var tabs = self.visibleTabs(), i = tabs.indexOf(document.activeElement);
          if (i < 0) return;
          e.preventDefault();
          tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length].focus();
        });
        if (!('IntersectionObserver' in window)) { this.start(); return; }
        /* probe once the box is within 600px of view. Horizon scrolls .page-wrapper
           (height:100dvh; overflow-y:auto) on ≥990px, not the document; rootMargin
           only widens the ROOT's box, so with the viewport as root the wrapper's
           clip would still fire the probe — just with no lead, when the box is
           already on screen. Rooting on the real scroller keeps the 600px head start. */
        var root = this.parentElement;
        while (root && !/auto|scroll/.test(getComputedStyle(root).overflowY)) root = root.parentElement;
        var io = new IntersectionObserver(function (entries) {
          if (!entries.some(function (en) { return en.isIntersecting; })) return;
          io.disconnect();
          self.start();
        }, { root: root || null, rootMargin: '600px 0px' });
        io.observe(this);
      }
      start() {
        var self = this, tabs = this.visibleTabs();
        var first = this.tabs.querySelector('[data-own]') || tabs[0];
        if (!first) { this.hidden = true; return; }
        first.tabIndex = 0; /* reachable by Tab while the probe runs; select() re-roves */
        if (navigator.connection && navigator.connection.saveData) { this.select(first.dataset.theme); return; }
        this.tabs.setAttribute('aria-busy', 'true');
        var probes = tabs.map(function (b) {
          return self.load(b.dataset.theme).catch(function () { /* stays visible; a tap retries */ });
        });
        /* paint the pre-selected theme as soon as ITS answer lands — the other probes
           only trim the tab row (no first-paint regression vs the old one-request path,
           and a stalled sibling can't hold the skeleton). Empty → wait for all of them
           so the fallback lands in one hop. */
        probes[tabs.indexOf(first)].then(function (entry) {
          if (!self.current && entry && entry.html) self.select(first.dataset.theme);
        });
        Promise.all(probes).then(function () {
          self.tabs.removeAttribute('aria-busy');
          if (self.current) return; /* already painted, or the shopper tapped mid-probe; select() owns it */
          self.pickFallback();
        });
      }
      visibleTabs() {
        return Array.prototype.filter.call(this.tabs.querySelectorAll('[data-theme]'), function (b) { return !b.hidden; });
      }
      url(handle) {
        return '/collections/' + handle + '?filter.p.product_type=' + encodeURIComponent(this.dataset.type || '') +
          '&sort_by=best-selling&section_id=mel-deal-rail';
      }
      /* one fetch per theme, shared by the probe and by taps (A→B→A reuses A's
         request); resolves to the cache entry and hides the tab when it is empty */
      load(handle) {
        var self = this;
        if (this.cache[handle]) return Promise.resolve(this.cache[handle]);
        if (this.inflight[handle]) return this.inflight[handle];
        var type = (this.dataset.type || '').trim().toLowerCase();
        var p = fetch(this.url(handle), { headers: { Accept: 'text/html' } })
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var src = doc.querySelector('[data-mel-rail-src]');
            /* review v15: the same-type promise must hold even if the Search &
               Discovery product-type facet is ever switched off (Shopify then
               ignores filter.p.product_type) — every tile carries its raw type. */
            var tiles = src ? Array.prototype.filter.call(src.querySelectorAll('.dtile'), function (t) {
              return t.dataset.melTile !== self.dataset.current && (t.dataset.melType || '').trim().toLowerCase() === type;
            }) : [];
            var limit = parseInt(self.dataset.limit, 10) || 6;
            var entry = { html: tiles.slice(0, limit).map(function (t) { return t.outerHTML; }).join('') };
            self.cache[handle] = entry;
            if (!entry.html) {
              /* every button for the handle — a handle repeated in the Theme list would
                 otherwise leave a visible twin that pickFallback re-picks forever */
              self.tabs.querySelectorAll('[data-theme="' + CSS.escape(handle) + '"]').forEach(function (t) { t.hidden = true; });
            }
            return entry;
          });
        p.then(function () { delete self.inflight[handle]; }, function () { delete self.inflight[handle]; });
        this.inflight[handle] = p;
        return p;
      }
      /* the product's next own theme with designs, else the first tab left; none → tabs + rail hide
         (the .promo header, tier chips and tally stay) */
      pickFallback() {
        var next = this.tabs.querySelector('[data-own]:not([hidden])') || this.visibleTabs()[0];
        if (next) this.select(next.dataset.theme); else this.hidden = true;
      }
      select(handle) {
        var self = this, hadFocus = this.tabs.contains(document.activeElement);
        this.current = handle;
        this.visibleTabs().forEach(function (b) {
          var on = b.dataset.theme === handle;
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
          if (on) {
            self.tabs.scrollLeft = Math.max(0, b.offsetLeft - 14);
            if (hadFocus) b.focus({ preventScroll: true });
          }
        });
        if (!this.cache[handle]) {
          this.rail.setAttribute('aria-busy', 'true');
          this.rail.innerHTML = this.skeleton;
        }
        this.load(handle).then(function (entry) {
          if (self.current !== handle) return;
          if (entry.html) self.render(handle); else self.pickFallback(); /* lazy mode, or tapped mid-probe */
        }, function () {
          if (self.current !== handle) return;
          self.rail.removeAttribute('aria-busy');
          self.rail.innerHTML = '<p class="drail-empty">Could not load this theme. Try again.</p>';
        });
      }
      render(handle) {
        /* client v15b: only the suggested listings — no "All N →" cap, nothing links off the page */
        var c = this.cache[handle], rail = this.rail;
        rail.innerHTML = c.html;
        var size = this.dataset.size, style = this.dataset.style;
        rail.querySelectorAll('select.qrow-size').forEach(function (sel) { melPickOption(sel, size, style); });
        rail.removeAttribute('aria-busy');
        rail.scrollLeft = 0;
        rail.dispatchEvent(new Event('scroll'));
      }
    });
  }

  /* ---- client v15: quiet adds. Forms flagged data-mel-quiet (deal-box
     tiles, cart-strip rows) and the quick-view dialog never auto-open the
     drawer (cart-drawer.js); instead <mel-toast> confirms — what was added,
     where the cart stands against the tiers, and CHECK OUT, which opens the
     drawer. Each such add is also pushed to the dataLayer tagged with the
     list it came from (deal_box | deal_popup | cart_upsell). ---- */
  var toastTimer = null;
  function melToast(main, sub) {
    var t = document.querySelector('mel-toast');
    if (!t) {
      t = document.createElement('mel-toast');
      t.setAttribute('role', 'status');
      t.innerHTML = '<div class="mtoast-tx"><b></b><span></span></div><button type="button" class="mtoast-go">Check out</button>';
      t.querySelector('.mtoast-go').addEventListener('click', function () {
        t.classList.remove('on');
        var drawer = document.getElementById('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      });
      document.body.appendChild(t);
    }
    t.querySelector('b').textContent = main;
    t.querySelector('span').textContent = sub || '';
    t.querySelector('span').hidden = !sub;
    t.classList.add('on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.classList.remove('on'); }, 5000);
  }
  var quietWired = false;
  function initQuietAdds() {
    if (quietWired) return;
    quietWired = true;
    document.addEventListener('shopify:cart:lines-update', function (e) {
      if (!e.promise || e.action !== 'add' || !(e.target instanceof Element)) return;
      var src = e.target;
      var quiet = src.closest('[data-mel-quiet]');
      if (!quiet) return;
      var listEl = src.closest('[data-mel-list]');
      var list = listEl ? listEl.dataset.melList : 'deal_popup';
      e.promise.then(function (r) {
        var d = r && r.detail;
        if (!d || d.didError) return;
        /* review v15: d.items is the WHOLE cart (newest-first only for new lines —
           re-adding a variant increments its existing line in place), so find the
           added variant from the event's own line list; fall back to items[0]. */
        var mid = e.lines && e.lines[0] && e.lines[0].merchandiseId ? String(e.lines[0].merchandiseId).split('/').pop() : '';
        var line = (mid && d.items && d.items.filter(function (i) { return String(i.variant_id) === mid; })[0]) || (d.items && d.items[0]);
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'mel_upsell_add', item_list_id: list, product_id: d.productId, variant_id: line && line.variant_id, quantity: d.itemCount });
        } catch (err) {}
        var drawer = document.getElementById('cart-drawer');
        if (drawer && drawer.hasAttribute('open')) return;
        var n = r.cart && r.cart.totalQuantity;
        var st = typeof n === 'number' ? melDealState(n) : null;
        var name = line ? line.product_title + (line.variant_title ? ' \u00B7 ' + line.variant_title : '') : 'Added';
        melToast('Added \u00B7 ' + name, st ? st.copy : '');
      }).catch(function () {});
    });
  }

  /* ---- <mel-slider>: one-cell-per-view snap slider (cart upsell, UGC wall).
     A custom element on purpose — nodes recreated by Horizon's cart section
     morphs upgrade automatically, so the drawer needs no re-init hook. The
     nav recounts cells on every sync because a morph can patch the track's
     children in place without reconnecting the element. ---- */
  if (!window.customElements.get('mel-slider')) {
    window.customElements.define('mel-slider', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.melInit) return;
        this.dataset.melInit = '1';
        var track = this.querySelector('[data-mel-track]');
        if (!track) return;
        var nav = document.createElement('div');
        nav.className = 'mel-slider__nav';
        nav.innerHTML = '<button type="button" aria-label="Previous">&#8249;</button>' +
          '<span class="mel-slider__dots" aria-hidden="true"></span>' +
          '<button type="button" aria-label="Next">&#8250;</button>';
        this.appendChild(nav);
        var prev = nav.children[0], dots = nav.children[1], next = nav.children[2];
        var count = function () { return track.children.length; };
        var idx = function () {
          return Math.min(count() - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
        };
        var go = function (i) {
          track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
        };
        prev.addEventListener('click', function () { go(Math.max(0, idx() - 1)); });
        next.addEventListener('click', function () { go(Math.min(count() - 1, idx() + 1)); });
        var sync = function () {
          var n = count(), i = idx();
          nav.hidden = n < 2;
          prev.disabled = i <= 0;
          next.disabled = i >= n - 1;
          while (dots.children.length > n) dots.removeChild(dots.lastChild);
          while (dots.children.length < n) dots.appendChild(document.createElement('i'));
          for (var k = 0; k < n; k++) dots.children[k].className = k === i ? 'on' : '';
        };
        track.addEventListener('scroll', function () { requestAnimationFrame(sync); }, { passive: true });
        window.addEventListener('resize', sync);
        sync();
        if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
        /* client v15e: mouse drag (touch swipes natively). Snap is suspended
           while dragging and the nearest cell is settled on release; a drag
           swallows the click that ends it so a row's link/Add is not fired. */
        var down = false, moved = false, sx = 0, sl = 0;
        track.addEventListener('pointerdown', function (e) {
          if (e.pointerType !== 'mouse' || e.button !== 0 || (e.target instanceof Element && e.target.closest('select,input,button'))) return;
          down = true; moved = false; sx = e.clientX; sl = track.scrollLeft;
        });
        track.addEventListener('pointermove', function (e) {
          if (!down) return;
          if (e.buttons === 0) { down = false; return; }
          var dx = e.clientX - sx;
          if (Math.abs(dx) > 4 && !moved) { moved = true; track.classList.add('grabbing'); track.style.scrollSnapType = 'none'; }
          if (moved) track.scrollLeft = sl - dx;
        });
        ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (t) {
          track.addEventListener(t, function () {
            if (!down) return;
            down = false;
            if (moved) { track.classList.remove('grabbing'); track.style.scrollSnapType = ''; go(idx()); }
            requestAnimationFrame(function () { moved = false; });
          });
        });
        track.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
      }
    });
  }

  /* ---- pre-launch review fix: Judge.me's reviews_for_widget returns two
     payload shapes — REVAMP installs answer {reviews:[...]} (the dev store),
     legacy installs answer {html} of .jdgm-rev markup (the production
     store). melJmReviews normalizes both into the array shape mel-revwall /
     mel-revfeat consume; the markup is Judge.me's server-sanitized fragment,
     the same HTML its own widget injects verbatim. ---- */
  var melJmReviews = function (d) {
    if (d && Array.isArray(d.reviews)) return d.reviews;
    if (!d || !d.html) return [];
    var doc = new DOMParser().parseFromString(d.html, 'text/html');
    return Array.prototype.map.call(doc.querySelectorAll('.jdgm-rev'), function (el) {
      var q = function (s) { return el.querySelector(s); };
      var rt = q('.jdgm-rev__rating');
      var ts = q('.jdgm-rev__timestamp');
      var body = q('.jdgm-rev__body');
      return {
        id: el.getAttribute('data-review-id') || '',
        rating: rt ? +rt.getAttribute('data-score') || 0 : 0,
        title: ((q('.jdgm-rev__title') || {}).textContent || '').trim(),
        body_html: body ? body.innerHTML : '',
        body: body ? body.textContent : '',
        /* '2026-05-15 05:12:51 UTC' -> ISO so every engine parses it; a
           missing timestamp falls back to epoch so date sorts stay total */
        created_at: (ts && ts.getAttribute('data-content') || '1970-01-01T00:00:00Z').replace(' ', 'T').replace(' UTC', 'Z'),
        reviewer_name: ((q('.jdgm-rev__author') || {}).textContent || '').trim(),
        product_title: el.getAttribute('data-product-title') || '',
        product_url: el.getAttribute('data-product-url') || '',
        pictures_urls: Array.prototype.map.call(el.querySelectorAll('.jdgm-rev__pic-link'), function (a) {
          var img = a.querySelector('.jdgm-rev__pic-img');
          return { small: (img && (img.getAttribute('data-src') || img.getAttribute('src'))) || a.href, original: a.href };
        })
      };
    });
  };

  /* ---- pre-launch review fix: the production catalog exposes hundreds of
     reviewed products, so an unbounded Promise.all was a browser fetch storm
     that Judge.me throttles. Run at most `size` requests at a time. ---- */
  var melJmPool = function (ids, worker, size) {
    var i = 0;
    var lane = function () {
      if (i >= ids.length) return Promise.resolve();
      return worker(ids[i++]).then(lane);
    };
    var lanes = [];
    for (var n = 0; n < size && n < ids.length; n++) lanes.push(lane());
    return Promise.all(lanes);
  };

  /* ---- pre-launch review fix: Judge.me groups sister products (e.g. the
     bomber-jacket family), so grouped product_ids answer with overlapping
     review sets — dedupe on the review's identity before rendering. ---- */
  var melJmKey = function (r) {
    return r.id || [r.created_at, r.reviewer_name, r.title || '', String(r.body || r.body_html || '').slice(0, 60)].join('|');
  };

  /* ---- client v6: happy-customers review wall. The app's all-reviews
     widget is disabled server-side (it spins forever, zero requests), so
     <mel-revwall> hydrates real reviews per product from Judge.me's public
     reviews_for_widget API — the endpoint the PDP widget itself pages
     through. Real data only; failures degrade to a status line. ---- */
  if (!window.customElements.get('mel-revwall')) {
    window.customElements.define('mel-revwall', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.init) return;
        this.dataset.init = '1';
        this.load();
      }
      load() {
        var self = this;
        var grid = self.querySelector('[data-rw-grid]');
        var status = self.querySelector('[data-rw-status]');
        var ids = [];
        try { ids = JSON.parse(self.querySelector('[data-rw-products]').textContent); } catch (err) { /* malformed list -> empty */ }
        if (!ids.length) { if (status) status.textContent = 'No reviews yet — be the first.'; return; }
        /* belt-and-braces with the section's Liquid cap — never storm */
        ids = ids.slice(0, 48);
        var base = 'https://cdn.judge.me/reviews/reviews_for_widget?platform=shopify&per_page=24&sort_by=created_at&sort_dir=desc&shop_domain='
          + encodeURIComponent(self.dataset.shop || location.hostname);
        var all = [], seenIds = {};
        var fetchProduct = function (id, page) {
          return fetch(base + '&product_id=' + id + '&page=' + page)
            .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
            .then(function (d) {
              melJmReviews(d).forEach(function (r) {
                var k = melJmKey(r);
                if (seenIds[k]) return;
                seenIds[k] = 1;
                all.push(r);
              });
              /* REVAMP ships a pagination object; legacy ships total_count */
              var pg = d.pagination;
              var pages = pg ? pg.total_pages : Math.ceil((+d.total_count || 0) / 24);
              var cur = pg ? pg.current_page : page; /* local page arg — termination never trusts the echo */
              if (cur < pages && cur < 5) return fetchProduct(id, page + 1);
            })
            .catch(function () { /* one product failing must not sink the wall */ });
        };
        melJmPool(ids, function (id) { return fetchProduct(id, 1); }, 6).then(function () {
          if (!all.length) { if (status) status.textContent = 'Reviews could not be loaded — please refresh.'; return; }
          all.sort(function (a, b) { return new Date(b.created_at) - new Date(a.created_at); });
          all = all.slice(0, 96); /* newest 96 — a full wall without a 1000-card DOM */
          if (status) status.remove();
          var frag = document.createDocumentFragment();
          all.forEach(function (r) { frag.appendChild(self.card(r)); });
          grid.appendChild(frag);
        });
      }
      card(r) {
        var el = document.createElement('article');
        el.className = 'rw-card';
        var head = document.createElement('div');
        head.className = 'rw-head';
        var stars = document.createElement('span');
        stars.className = 'rw-stars';
        stars.textContent = '★★★★★'.slice(0, Math.max(1, Math.min(5, Math.round(+r.rating || 5))));
        var date = document.createElement('time');
        date.className = 'rw-date';
        try {
          date.textContent = new Date(r.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        } catch (err) { /* unparseable date stays blank */ }
        head.append(stars, date);
        el.appendChild(head);
        if (r.title) {
          var h = document.createElement('h3');
          h.textContent = r.title;
          el.appendChild(h);
        }
        var body = document.createElement('div');
        body.className = 'rw-body';
        /* body_html is Judge.me's server-sanitized fragment — the same HTML
           its own widget injects verbatim */
        body.innerHTML = r.body_html || '';
        el.appendChild(body);
        var pics = Array.isArray(r.pictures_urls) ? r.pictures_urls : [];
        if (pics.length) {
          var rail = document.createElement('div');
          rail.className = 'rw-pics';
          pics.slice(0, 4).forEach(function (p) {
            if (!p || !p.small) return;
            var a = document.createElement('a');
            a.href = p.original || p.huge || p.small;
            a.target = '_blank';
            a.rel = 'noopener';
            var img = document.createElement('img');
            img.src = p.small;
            img.loading = 'lazy';
            img.alt = r.title || 'Review photo';
            img.width = 96;
            img.height = 96;
            a.appendChild(img);
            rail.appendChild(a);
          });
          if (rail.children.length) el.appendChild(rail);
        }
        var foot = document.createElement('p');
        foot.className = 'rw-foot';
        var name = document.createElement('b');
        name.textContent = r.reviewer_name || 'Anonymous';
        foot.appendChild(name);
        if (r.product_title) {
          foot.appendChild(document.createTextNode(' · about '));
          var link = document.createElement('a');
          link.href = r.product_url || '#';
          link.textContent = r.product_title;
          foot.appendChild(link);
        }
        el.appendChild(foot);
        return el;
      }
    });
  }

  /* ---- client v8: homepage featured reviews. The app's featured_carousel
     block rendered tiny thumbs in a sea of whitespace, so <mel-revfeat>
     builds its own press cards: Liquid supplies id→{plate,url,title} for
     every reviewed product, JS pulls page 1 per product from the same
     public endpoint mel-revwall uses, ranks photo → rating → substance,
     and shows up to 6 cards (one per product first, for variety). ---- */
  if (!window.customElements.get('mel-revfeat')) {
    window.customElements.define('mel-revfeat', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.init) return;
        this.dataset.init = '1';
        this.load();
      }
      load() {
        var self = this;
        var grid = self.querySelector('[data-rf-grid]');
        var status = self.querySelector('[data-rf-status]');
        var map = {};
        try { map = JSON.parse(self.querySelector('[data-rf-products]').textContent) || {}; } catch (err) { /* malformed map -> empty */ }
        var ids = Object.keys(map);
        if (!ids.length) { if (status) status.textContent = 'No reviews yet — be the first.'; return; }
        /* belt-and-braces with the section's Liquid cap — never storm */
        ids = ids.slice(0, 24);
        var base = 'https://cdn.judge.me/reviews/reviews_for_widget?platform=shopify&per_page=24&sort_by=created_at&sort_dir=desc&shop_domain='
          + encodeURIComponent(self.dataset.shop || location.hostname);
        var all = [], seenIds = {};
        melJmPool(ids, function (id) {
          return fetch(base + '&product_id=' + id + '&page=1')
            .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
            .then(function (d) {
              melJmReviews(d).forEach(function (r) {
                var k = melJmKey(r);
                if (seenIds[k]) return;
                seenIds[k] = 1;
                r._pid = id;
                all.push(r);
              });
            })
            .catch(function () { /* one product failing must not sink the section */ });
        }, 6).then(function () {
          if (!all.length) { if (status) status.textContent = 'Reviews could not be loaded — please refresh.'; return; }
          var score = function (r) {
            var pics = Array.isArray(r.pictures_urls) ? r.pictures_urls.length : 0;
            var text = String(r.body || '').replace(/<[^>]+>/g, '');
            return (pics ? 100 : 0) + (+r.rating || 0) * 10 + Math.min(text.length, 220) / 25;
          };
          all.sort(function (a, b) { return score(b) - score(a); });
          var picks = [], seen = {};
          all.forEach(function (r) { if (picks.length < 6 && !seen[r._pid]) { seen[r._pid] = 1; picks.push(r); } });
          all.forEach(function (r) { if (picks.length < 6 && picks.indexOf(r) < 0) picks.push(r); });
          if (status) status.remove();
          var frag = document.createDocumentFragment();
          picks.forEach(function (r) { frag.appendChild(self.card(r, map[r._pid])); });
          grid.appendChild(frag);
        });
      }
      card(r, p) {
        var el = document.createElement('article');
        el.className = 'rf-card';
        var pics = Array.isArray(r.pictures_urls) ? r.pictures_urls : [];
        var photo = pics.length && (pics[0].huge || pics[0].original || pics[0].small);
        var a = document.createElement('a');
        a.className = 'rf-im';
        a.href = (p && p.u) || r.product_url || '#';
        var img = document.createElement('img');
        img.src = photo || (p && p.i) || '';
        img.loading = 'lazy';
        img.alt = (p && p.t) || 'Customer photo';
        img.width = 560;
        img.height = 700;
        a.appendChild(img);
        el.appendChild(a);
        var bd = document.createElement('div');
        bd.className = 'rf-bd';
        var top = document.createElement('div');
        top.className = 'rf-top';
        var stars = document.createElement('span');
        stars.className = 'rw-stars';
        stars.textContent = '★★★★★'.slice(0, Math.max(1, Math.min(5, Math.round(+r.rating || 5))));
        var ver = document.createElement('span');
        ver.className = 'rf-ver';
        ver.textContent = '✓ Verified buyer';
        top.append(stars, ver);
        bd.appendChild(top);
        var q = document.createElement('div');
        q.className = 'rf-q';
        /* body_html is Judge.me's server-sanitized fragment — the same HTML
           its own widget injects verbatim (mel-revwall precedent) */
        q.innerHTML = r.body_html || '';
        bd.appendChild(q);
        var who = document.createElement('p');
        who.className = 'rf-who';
        var name = document.createElement('b');
        name.textContent = r.reviewer_name || 'Anonymous';
        who.appendChild(name);
        if (p && p.t) {
          who.appendChild(document.createTextNode(' on '));
          var link = document.createElement('a');
          link.href = p.u || '#';
          link.textContent = p.t;
          who.appendChild(link);
        }
        bd.appendChild(who);
        el.appendChild(bd);
        return el;
      }
    });
  }

  /* ---- quick-row variant select → live price (client v3: options carry
     data-price/data-compare instead of "+$x" in the label). Delegated, so it
     survives every drawer morph. ---- */
  document.addEventListener('change', function (e) {
    var sel = e.target;
    if (!(sel instanceof HTMLSelectElement) || !sel.classList.contains('qrow-size')) return;
    var opt = sel.options[sel.selectedIndex];
    var row = sel.closest('.qrow, .dtile');
    var out = row && row.querySelector('.qrow-price');
    if (!opt || !out || !opt.dataset.price) return;
    if (opt.dataset.compare) {
      var sale = document.createElement('span');
      sale.className = 'sale';
      sale.textContent = opt.dataset.price;
      var was = document.createElement('s');
      was.textContent = opt.dataset.compare;
      out.replaceChildren(sale, document.createTextNode(' '), was);
    } else {
      out.textContent = opt.dataset.price;
    }
  });

  /* ---- client v6: the PDP star row scrolls to the Judge.me widget. The
     revamp widget renders no #judgeme_product_reviews_block anchor, so the
     .mel-rating href never matched — intercept and scroll to the widget box
     (scroll-margin-top in CSS clears the sticky masthead). ---- */
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    var link = e.target.closest('.mel-rating');
    if (!link) return;
    var widget = document.querySelector('.jdgm-widget.jdgm-review-widget');
    if (!widget) return;
    e.preventDefault();
    widget.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
  });

  /* ---- client v6: swap [data-mel-fallback] images whose primary src 404s
     (a seeded collection featured_image can point at a dead CDN file).
     Capture phase — error events don't bubble. ---- */
  document.addEventListener('error', function (e) {
    var img = e.target;
    if (!(img instanceof HTMLImageElement) || !img.dataset.melFallback) return;
    img.src = img.dataset.melFallback;
    delete img.dataset.melFallback;
  }, true);

  /* ---- client v5: collapsed-by-default collection filters. The toggle
     flips body.mel-fo (the body is outside facet section morphs, so the
     panel stays open across filter re-renders). Delegated click survives
     morphs; <mel-ftog> re-syncs aria-expanded when the element is
     re-inserted, and the observer catches in-place attribute patches
     (Horizon's morph rewrites the server-rendered "false" onto the open
     toggle without reconnecting it). ---- */
  function syncFtog() {
    var open = document.body.classList.contains('mel-fo');
    document.querySelectorAll('[data-mel-filter-toggle]').forEach(function (b) {
      var want = open ? 'true' : 'false';
      if (b.getAttribute('aria-expanded') !== want) b.setAttribute('aria-expanded', want);
    });
  }
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element) || !e.target.closest('[data-mel-filter-toggle]')) return;
    document.body.classList.toggle('mel-fo');
    syncFtog();
  });
  if (!window.customElements.get('mel-ftog')) {
    window.customElements.define('mel-ftog', class extends HTMLElement {
      connectedCallback() { syncFtog(); }
    });
  }
  new MutationObserver(syncFtog).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-expanded'],
  });

  /* ---- track-order form: open the carrier-agnostic 17TRACK tracker ---- */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-mel-track-form')) return;
    e.preventDefault();
    var num = (new FormData(form).get('num') || '').toString().trim();
    if (!num) return;
    window.open('https://t.17track.net/en#nums=' + encodeURIComponent(num), '_blank', 'noopener');
  });

  /* ---- story-section "Add to cart" (client v10): the CTA returns the
     shopper to the buy column so they can pick a size — the PDP form owns
     the actual add ---- */
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element) || !e.target.closest('[data-mel-buy]')) return;
    var form = document.querySelector('product-form-component');
    if (!form) return;
    form.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
    var section = form.closest('.shopify-section');
    var size = section && section.querySelector(
      'variant-picker input[type="radio"]:checked, variant-picker select'
    );
    if (size) size.focus({ preventScroll: true });
  });

  /* ---- footer columns: open everywhere, accordion only below 900px ---- */
  function initFooter(root) {
    root.querySelectorAll('.mel-footer').forEach(function (f) {
      if (f.dataset.melInit) return;
      f.dataset.melInit = '1';
      var mq = window.matchMedia('(min-width: 900px)');
      var apply = function () {
        f.querySelectorAll('details').forEach(function (d, i) {
          if (mq.matches) d.open = true;
          else d.open = i === 0;
        });
      };
      apply();
      mq.addEventListener('change', apply);
    });
  }

  function init(root) {
    root = root || document;
    initMarquee(root);
    initRails(root);
    initShipDates(root);
    initReveal();
    initMobileMenu(root);
    initPdpSelect();
    initDealTally();
    initQuietAdds();
    initFooter(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  /* theme editor re-renders a section's DOM; re-bind inside it */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
